import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Styled from 'styled-components';
import Theme from '../common/NekoTheme';
import { ProOnly } from '../label/ProOnly';
import { useOutsideClick } from '../hooks';

import { Icon } from '@iconify/react';
import checkboxBlankCircleOutline from '@iconify/icons-mdi/checkbox-blank-circle-outline';
import recordCircle from '@iconify/icons-mdi/record-circle';
import chevronDown from '@iconify/icons-mdi/chevron-down';
import chevronUp from '@iconify/icons-mdi/chevron-up';

const Select = Styled.div`
  border-radius: 8px;
  position: relative;
  user-select: none;
  cursor: pointer;

  &.show-options {
    background-color: ${Theme.blue};
    border-radius: 8px 8px 0 0;
  }
`;

const SelectedOption = Styled.div`
  align-items: center;
  background-color: ${Theme.white};
  border: 2px solid ${Theme.blue};
  border-radius: 8px;
  display: flex;
  font-family: ${Theme.fontFamily};
  font-size: 14px;
  padding: 0 5px 0 10px;
  box-sizing: border-box;
  height: 30px;

  .rightContent {
    align-items: center;
    display: flex;
    margin-left: auto;
  }
`;

const Description = Styled.div`
  font-family: ${Theme.fontFamily};
  font-size: 12px;
  line-height: 14px;
  background-color: ${Theme.white};
  margin-top: 8px;
`;

const Options = Styled.div`
  border-radius: 8px;
  overflow: hidden;
  padding-top: 28px;
  position: absolute;
  top: 0;
  z-index: 10;
  width: 100%;
  transition: all 0.2s ease-in;

  &.hidden {
    opacity: 0;
  }
`;

const OptionsContainer = Styled.div`
  background-color: ${Theme.white};
`;

/**
 * @param {string} name
 */
const StyledNekoSelect = props => {
  const { name, value, scrolldown = false, isPro = false, onChange } = props;
  const currentOption = React.Children.toArray(props.children)
    .find(child => child.props.value === value)?.props;
  const label = currentOption?.label || 'Select';
  const description = currentOption?.description;
  const requirePro = currentOption?.requirePro || false;
  const id = scrolldown ? props.id : currentOption?.id;

  const [ showOptions, toggleOptions ] = useState(false);
  const componentRef = useOutsideClick(() => { toggleOptions(false) });
  const showUpProOnly = requirePro && !isPro;

  const onOptionClick = (newValue) => {
    if (newValue !== value) {
      if (!onChange) {
        console.log(`The onChange handler it not set for this checkbox.`, props);
        return;
      }
      onChange(newValue, id);
    }
    if (scrolldown) {
      toggleOptions(false);
    }
  }

  const children = React.Children.map(props.children, child => {
    return React.cloneElement(child, {
      name: child.props.name || name,
      checked: child.props.value === value,
      onClick: onOptionClick,
      scrolldown,
      isPro
    });
  });

  return scrolldown
    ? <Select id={id} ref={componentRef}
        onClick={() => toggleOptions(!showOptions)}
        className={[ 'nui-select', showOptions ? 'show-options' : '']}
        childrenLength={children.length}
      >
        <SelectedOption>
          {label}
          <div className="rightContent">
            {showUpProOnly && <ProOnly />}
            <Icon icon={showOptions ? chevronUp : chevronDown} width="24" />
          </div>
        </SelectedOption>
        {description && <Description>{description}</Description>}
        {showOptions &&
          <Options className={showOptions ? '' : 'hidden'}>
            <OptionsContainer>{[children]}</OptionsContainer>
          </Options>
        }
      </Select>
    : children;
};

const RadioOption = Styled.div`
  font-family: ${Theme.fontFamily};
  margin-bottom: 6px;

  input {
    display: none;
  }

  label {
    cursor: pointer;
    display: flex;
  }

  .inner-container {
    margin-left: 4px;

    .label {
      display: block;
      font-size: 14px;
      line-height: 17px;
      padding-top: 4.5px;
      padding-bottom: 4px;
    }
    .description {
      display: block;
      font-size: 12px;
    }
  }

  &.disabled {
    color: ${Theme.disabledForm};

    label {
      cursor: default;
    }
  }
`;

const SelectOption = Styled.div`
  background-color: ${Theme.blue};
  cursor: pointer;
  font-family: ${Theme.fontFamily};
  padding: 8px 12px;

  &:hover {
    filter: brightness(80%);
  }

  input {
    display: none;
  }

  .option {
    align-items: center;
    color: ${Theme.white};
    display: flex;
    justify-content: space-between;
    font-family: ${Theme.fontFamily};
    font-size: 14px;
    line-height: 17px;
  }

  &.disabled {
    background-color: rgba(${Theme.yellowRGB}, 0.25);
    cursor: default;
    pointer-events: none;
  }
`;

/**
 * @param {string} id
 * @param {string} name
 * @param {string} value
 * @param {bool} checked
 * @param {string} label
 * @param {string} description
 * @param {function} onClick
 */
const StyledNekoOption = props => {
  const { id, name, value, checked, label, description, onClick, scrolldown, isPro = false, requirePro = false } = props;

  const showUpProOnly = requirePro && !isPro;
  const disabledClass = showUpProOnly ? 'disabled' : '';

  const radioOption = (
    <RadioOption className={`nui-select ${props.className || ''} ${disabledClass}`}>
      <input id={id} name={name} type="radio" value={value} defaultChecked={checked} onClick={(e) => onClick(e.target.value)} disabled={showUpProOnly} />
      <label htmlFor={id}>
        <Icon icon={checked ? recordCircle : checkboxBlankCircleOutline} width="24px" color={showUpProOnly ? Theme.disabledForm : Theme.blue}/>
        <div className="inner-container">
          <span className="label">{label}<ProOnly className="inline" style={{ top: -1 }} show={showUpProOnly} /></span>
          {description ? <small className="description">{description}</small> : null}
        </div>
      </label>
    </RadioOption>
  );

  const selectOption = (
    <SelectOption className={`nui-select ${props.className || ''} ${disabledClass}`}>
      <input id={id} name={name} type="radio" value={value} defaultChecked={checked} disabled={showUpProOnly} />
      <div className="option" onClick={() => onClick(value)}>
        {label}
        <ProOnly show={showUpProOnly} />
      </div>
    </SelectOption>
  );

  return scrolldown ? selectOption : radioOption;
};

/**
 * The NekoSelect has 2 types, radio (default) or select.
 */
const NekoSelect = (props) => {
  return (<StyledNekoSelect {...props} />);
}

NekoSelect.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  scrolldown: PropTypes.bool,
  isPro: PropTypes.bool,
  onChange: PropTypes.func
};

NekoSelect.propTypes = {
  id: undefined,
  name: undefined,
  scrolldown: false,
  isPro: false,
  onChange: undefined
};

const NekoOption = (props) => {
  return (<StyledNekoOption {...props} />);
}

NekoOption.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  checked: PropTypes.bool,
  label: PropTypes.string,
  description: PropTypes.string,
  onClick: PropTypes.func,
  scrolldown: PropTypes.bool,
  isPro: PropTypes.bool,
  requirePro: PropTypes.bool
};

NekoOption.defaultProps = {
  id: undefined,
  name: undefined,
  value: undefined,
  checked: false,
  label: undefined,
  description: undefined,
  onClick: undefined,
  scrolldown: false,
  isPro: false,
  requirePro: false
};

export { NekoSelect, NekoOption };
